import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { SectionCatalogProps } from 'src/components/SectionCatalogMain/src/helperComponents/SectionCatalogWithSearch';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { Catalog } from 'src/types/backendContent';

import { SectionCatalogMain } from '../../components/SectionCatalogMain/src';
import { useAnalytics } from '../../hooks/useAnalytics';
import { mapProductWithCategories } from '../../layout/Header/components/Products/utils/mapProductWithCategories';
import styles from './styles.module.scss';

interface CatalogBlockProps {
  id?: string;
  title: string;
  categories: Catalog['categories'];
  products: Catalog['products'];
  platformFilter?: string;
  allSolutionsLength?: number;
  allProductsLength?: number;
}

export function CatalogBlock({
  id,
  title,
  categories,
  products,
  allSolutionsLength,
  allProductsLength,
  platformFilter,
}: CatalogBlockProps) {
  const [activeCategory, setActiveCategory] = useState(categories[0].id);

  const router = useRouter();
  const { clickAnalytics, customAnalytics } = useAnalytics();

  const layoutType = useLayoutType();

  const handleCategoryFilterChange = (categoryId: string) => {
    const foundCategory =
      categories.find((item) => item.id === categoryId)?.title ?? categoryId;
    setActiveCategory(categoryId);
    clickAnalytics({
      action: 'click',
      clickZone: 'body',
      clickElement: 'filter-select',
      clickContent: foundCategory,
      uniqueId: `catalog-product-categories-filter-select-${foundCategory}`,
      transitionType: 'inside-link',
      clickZoneCategory: 'site-body',
      transitionTypeMetrics: 'internal',
    });
  };

  const productCategoriesMap = useCallback(
    () =>
      mapProductWithCategories({
        categoriesByPlatform: categories,
        productsItems: products,
        searched: products,
        titleFilter: '',
        onClickElement: (href: string, title: string, productType) => {
          clickAnalytics({
            action: href,
            clickZone: 'body',
            clickElement: 'cardclick',
            clickContent: title,
            uniqueId: `catalog-product-${title}`,
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
            transitionUrl: href,
            transitionTypeMetrics: 'internal',
          });

          //TODO: удалить customAnalytics после тестирования
          if (productType === 'solutions') {
            customAnalytics('AB_solutions_card_clicks', ['']);
          }

          if (href.startsWith('/')) {
            router.push(href);
          }
        },
      }),
    [categories, clickAnalytics, customAnalytics, products, router],
  );

  const footerLinks: SectionCatalogProps['footer'] = [
    {
      label: `Все сервисы ${allProductsLength ? `(${allProductsLength})` : ''}`,
      href: '/products',
      onClick: (e) => {
        e.preventDefault();

        clickAnalytics({
          action: '/products',
          clickZone: 'body',
          clickElement: 'button',
          clickContent: 'Все сервисы',
          uniqueId: 'additional-links-products',
          transitionType: 'inside-link',
          clickZoneCategory: 'site-body',
          transitionUrl: '/products',
          transitionTypeMetrics: 'internal',
        });

        router.push('/products');
      },
    },
    {
      label: `Все решения ${
        allSolutionsLength ? `(${allSolutionsLength})` : ''
      }`,
      href: '/solutions',
      onClick: (e) => {
        e.preventDefault();

        clickAnalytics({
          action: 'click',
          clickZone: 'body',
          clickElement: 'button',
          clickContent: 'Все решения',
          uniqueId: 'additional-links-solutions',
          transitionType: 'inside-link',
          clickZoneCategory: 'site-body',
        });

        router.push('/solutions');
      },
    },
    {
      label: 'Маркетплейс',
      href: '/marketplace',
      onClick: (e) => {
        e.preventDefault();

        clickAnalytics({
          action: '/marketplace',
          clickZone: 'body',
          clickElement: 'button',
          clickContent: 'Маркетплейс',
          uniqueId: 'additional-links-marketplace',
          transitionType: 'inside-link',
          clickZoneCategory: 'site-body',
          transitionUrl: '/marketplace',
          transitionTypeMetrics: 'internal',
        });

        router.push('/marketplace');
      },
    },
  ];

  return (
    <SectionCatalogMain
      className={styles.root}
      layoutType={layoutType}
      isOldCard
      sectionCatalogProps={{
        id,
        title,
        categories: productCategoriesMap(),
        onCategorySelect: handleCategoryFilterChange,
        selectedCategory: activeCategory,
        layoutType,
        platformFilter: platformFilter ?? 'evolution',
        footer: footerLinks,
      }}
    />
  );
}
